import { Check, ChevronDown, Search, XIcon } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import ExportTray from '@/assets/icons/export-tray.svg?react';
import ImportTray from '@/assets/icons/import-tray.svg?react';
import csvDescriptionImage1 from '@/assets/images/csv-description-1.png?url';
import csvDescriptionImage2 from '@/assets/images/csv-description-2.png?url';
import dropzoneIcon from '@/assets/images/dropzone-icon.png?url';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { Input } from '@/components/atoms/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { Separator } from '@/components/atoms/separator';
import { DropzoneSingle } from '@/components/molecules/DropzoneSingle';
import {
  DEFAULT_LOCATION_METRICS,
  METRIC_CHECKBOX_CLASSNAME,
  METRIC_LABEL_CLASSNAME,
  METRICS_LIST,
  type MetricsList,
} from '@/features/locations/constants';
import { useAddLocationsMultipart } from '@/features/locations/services/locations.api';
import { WORKSPACE_PICKER_SURFACE } from '@/features/workspaces/constants/workspacePicker';
import {
  useAllWorkspacesQuery,
  useUploadWorkspaceCSV,
} from '@/features/workspaces/services/workspace.api';
import { filterWorkspacesByName } from '@/features/workspaces/utils/filterWorkspaces';
import { useMixpanel } from '@/hooks/useMixpanel';
import { useToastOnNewWarnings } from '@/hooks/useToastOnNewWarnings';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { cn } from '@/libs/utils/cn';
import { formatBytes } from '@/libs/utils/formatBytes';
import {
  toggleCapturedAadtSelection,
  toggleForecastSelection,
} from '@/features/locations/utils/metricSelection';

/** Same default set the map requests when a location is added. */
const BATCH_LOCATION_METRICS: MetricsList[] = [...DEFAULT_LOCATION_METRICS];

const METRIC_LABELS: Record<MetricsList, string> = {
  [METRICS_LIST.MAX_AADT]: 'Max AADT',
  [METRICS_LIST.CLOSEST_AADT]: 'Closest AADT',
  [METRICS_LIST.SUM_AADT]: 'Sum AADT',
  [METRICS_LIST.POPULATION_DENSITY]: 'Population density',
  [METRICS_LIST.LOCAL_POPULATION_DENSITY]: 'Local population density',
  [METRICS_LIST.NUMBER_HPC_STATIONS]: 'Number of HPC stations',
  [METRICS_LIST.AVERAGE_CHARGES_STATION_PER_DAY]: 'Avg. charges/station/day (other CPOs)',
  [METRICS_LIST.HIGHEST_PERFORMING_COMPETITOR]: 'Highest performing competitor',
  [METRICS_LIST.CAPTURED_AADT]: 'Captured AADT',
  [METRICS_LIST.FORECAST]: 'Demand forecast',
};

interface BatchUploadCSVModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onProjectCreated: (projectId: string) => void;
}

interface IUploadedFile {
  name: string;
  file: File;
}

interface CSVFormValues {
  'locations-csv': IUploadedFile | null;
  metrics: MetricsList[];
  workspaceId: string | null;
}

/** Strip a single trailing file extension so `My Locations.csv` → `My Locations`. */
function stripFileExtension(filename: string): string {
  return filename.replace(/\.[^./\\]+$/, '');
}

export function BatchUploadCSVModal(props: BatchUploadCSVModalProps) {
  const { open, setOpen, onProjectCreated } = props;

  const form = useForm<CSVFormValues>({
    defaultValues: {
      'locations-csv': null,
      metrics: [...BATCH_LOCATION_METRICS],
      workspaceId: null,
    },
  });

  const [uploadingFile, setUploadingFile] = useState<{
    file: File;
    progress: number;
  } | null>(null);
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState(false);
  const [workspaceFilter, setWorkspaceFilter] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedMetrics = form.watch('metrics');
  const capturedAadtSelected = selectedMetrics.includes(METRICS_LIST.CAPTURED_AADT);
  const forecastSelected = selectedMetrics.includes(METRICS_LIST.FORECAST);

  // Stacking, per-metric advisory warnings. Demand forecast forces Captured AADT on,
  // so selecting it surfaces both the dependency note and the Captured AADT perf warning.
  const metricWarnings: string[] = [];
  if (forecastSelected) {
    metricWarnings.push('Demand forecast requires Captured AADT.');
  }
  if (capturedAadtSelected) {
    metricWarnings.push('Captured AADT slows down compute.');
  }
  useToastOnNewWarnings(metricWarnings, open);

  const selectedWorkspaceId = form.watch('workspaceId');

  const { data: workspacesData } = useAllWorkspacesQuery();
  const allWorkspaces = workspacesData?.workspaces ?? [];
  const hasWorkspaces = allWorkspaces.length > 0;
  const filteredWorkspaces = useMemo(
    () => filterWorkspacesByName(allWorkspaces, workspaceFilter),
    [allWorkspaces, workspaceFilter],
  );
  const selectedWorkspace = allWorkspaces.find((ws) => ws.id === selectedWorkspaceId) ?? null;

  const isSaveDisabled = !form.watch('locations-csv') || selectedMetrics.length === 0;

  const uploadWorkspaceCSV = useUploadWorkspaceCSV();
  const addLocationsMultipart = useAddLocationsMultipart();

  // Analytics tracking
  const trackAction = useMixpanel((state) => state.trackAction);

  const workspaceFilterId = useId();
  const menuId = useId();
  const workspaceHelpId = useId();

  // Arrow-key roving across the visible menu items; Escape closes and refocuses
  // the trigger. Mirrors WorkspaceSwitcher's keyboard pattern.
  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      setWorkspacePickerOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;

    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const next = (current + delta + items.length) % items.length;
    items[next]?.focus();
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      form.reset({
        'locations-csv': null,
        metrics: [...BATCH_LOCATION_METRICS],
        workspaceId: null,
      });
      setUploadingFile(null);
      setWorkspacePickerOpen(false);
      setWorkspaceFilter('');
    }
  }

  function toggleMetric(
    field: { value: MetricsList[]; onChange: (value: MetricsList[]) => void },
    key: MetricsList,
    checked: boolean,
  ) {
    const withoutKey = field.value.filter((k) => k !== key);
    field.onChange(checked ? [...withoutKey, key] : withoutKey);
  }

  async function onSubmit(values: CSVFormValues) {
    const file = values['locations-csv']?.file;
    const workspaceId = values.workspaceId;
    if (!file) return;

    // Start the upload when submit is clicked
    setUploadingFile({
      file,
      progress: 0,
    });

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      for (const m of values.metrics) {
        formData.append('metrics', m);
      }

      // Backend default keeps the extension, so strip ours before sending.
      formData.append('project_name', stripFileExtension(file.name));

      const onProgress = (progress: number) => {
        setUploadingFile((prev) => (prev ? { ...prev, progress } : null));
      };

      // No workspace selected → standalone project via POST /locations/metrics.
      const uploadedProject = workspaceId
        ? await uploadWorkspaceCSV.mutateAsync({ workspaceId, formData, onProgress })
        : await addLocationsMultipart.mutateAsync({
            formMetricsAndLocations: formData,
            onProgress,
          });

      // Track CSV upload success
      trackAction(ACTION_TRACKED.CSV_UPLOAD, {
        project_id: uploadedProject.id,
        success: true,
        file_name: file.name,
        file_size: file.size,
      });
      setUploadingFile(null);
      handleOpenChange(false);

      // Notify parent component about the newly created project
      onProjectCreated(uploadedProject.id);
    } catch (error) {
      // Track CSV upload failure
      trackAction(ACTION_TRACKED.CSV_UPLOAD, {
        success: false,
        file_name: file.name,
        file_size: file.size,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });

      setUploadingFile(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[90vw] max-w-[700px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <DialogHeader className="gap-3">
              <DialogTitle className="flex items-center gap-2 font-normal text-primary-700">
                <ImportTray className="size-4" aria-hidden="true" />
                Import CSV
              </DialogTitle>
              <details className="group bg-brand-50 rounded text-primary-700">
                <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-2.5 text-sm font-bold [&::-webkit-details-marker]:hidden">
                  How to format your CSV
                  <ChevronDown
                    className="size-4 shrink-0 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <div className="flex flex-col gap-3 px-4 pb-4">
                  <div className="flex gap-4">
                    <div className="min-w-0 flex-1">
                      <img
                        src={csvDescriptionImage1}
                        alt="CSV upload format example 1"
                        className="mx-auto h-auto max-h-28 w-full object-contain"
                      />
                    </div>
                    <div className="flex flex-col items-center justify-center text-primary-600 font-medium text-lg">
                      <div className="w-px h-8 bg-primary-700"></div>
                      <span className="px-2">OR</span>
                      <div className="w-px h-8 bg-primary-700"></div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <img
                        src={csvDescriptionImage2}
                        alt="CSV upload format example 2"
                        className="mx-auto h-auto max-h-28 w-full object-contain"
                      />
                    </div>
                  </div>
                  <h3 className="text-primary-700 text-base font-bold">
                    Format a CSV with a maximum of 5 000 locations using either:
                  </h3>
                  <ol className="text-primary-default text-[15px] font-normal space-y-1">
                    <li>
                      1. One column: <strong>Address</strong> only or
                    </li>
                    <li>
                      2. Two columns: <strong>Latitude</strong> and <strong>Longitude</strong>
                    </li>
                  </ol>
                  <Button asChild variant="outline" className="w-fit mx-auto">
                    <a
                      href="/SpotOn_template.csv"
                      download
                      className="rounded-lg"
                      aria-label="Download CSV template"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded bg-emerald-500 text-white text-xs font-bold">
                            CSV
                          </div>
                          <span className="text-primary-700 font-medium">SpotOn_template.csv</span>
                        </div>
                        <ExportTray className="h-5 w-5 text-primary-600" />
                      </div>
                    </a>
                  </Button>
                </div>
              </details>
            </DialogHeader>

            <DropzoneSingle
              onDrop={(file) => {
                form.setValue('locations-csv', {
                  name: file.name,
                  file,
                });
              }}
              isUploadingFile={uploadingFile}
              hasFile={!!form.watch('locations-csv')}
            />

            {form.watch('locations-csv') && (
              <FormField
                control={form.control}
                name={'locations-csv'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary-700 font-normal text-base">
                      Your imported files
                    </FormLabel>
                    {field.value && (
                      <FormControl>
                        <div className="flex border border-primary-300 min-h-16 rounded-sm items-center p-3 gap-3">
                          <img src={dropzoneIcon} className="size-9" alt="CSV dropzone logo" />
                          <div className="flex flex-col">
                            <span className="text-sm text-primary-700">{field.value.name}</span>
                            <span className="text-[0.625rem] text-primary-600">
                              {formatBytes(field.value.file.size)} - File size
                            </span>
                          </div>
                          <div className="flex w-full justify-end">
                            <Button
                              type="button"
                              aria-label="Remove file"
                              className="cursor-pointer"
                              variant="ghost"
                              onClick={(e) => {
                                e.preventDefault();
                                form.setValue('locations-csv', null);
                              }}
                            >
                              <XIcon />
                            </Button>
                          </div>
                        </div>
                      </FormControl>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="metrics"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-primary-700 font-normal text-sm">
                    Metrics to include in the output
                  </FormLabel>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {BATCH_LOCATION_METRICS.map((key) => {
                      const checkboxId = `batch-metric-${key}`;
                      return (
                        <div key={key} className="flex items-center gap-1.5">
                          <Checkbox
                            id={checkboxId}
                            checked={field.value.includes(key)}
                            onCheckedChange={(checked) =>
                              toggleMetric(field, key, checked === true)
                            }
                            className={METRIC_CHECKBOX_CLASSNAME}
                          />
                          <label htmlFor={checkboxId} className={METRIC_LABEL_CLASSNAME}>
                            {METRIC_LABELS[key]}
                          </label>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="batch-metric-captured_aadt"
                        checked={field.value.includes(METRICS_LIST.CAPTURED_AADT)}
                        disabled={forecastSelected}
                        onCheckedChange={(checked) =>
                          field.onChange(toggleCapturedAadtSelection(field.value, checked === true))
                        }
                        className={METRIC_CHECKBOX_CLASSNAME}
                      />
                      <label
                        htmlFor="batch-metric-captured_aadt"
                        className={METRIC_LABEL_CLASSNAME}
                      >
                        {METRIC_LABELS[METRICS_LIST.CAPTURED_AADT]}
                      </label>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="batch-metric-forecast"
                        checked={forecastSelected}
                        onCheckedChange={(checked) =>
                          field.onChange(toggleForecastSelection(field.value, checked === true))
                        }
                        className={METRIC_CHECKBOX_CLASSNAME}
                      />
                      <label htmlFor="batch-metric-forecast" className={METRIC_LABEL_CLASSNAME}>
                        {METRIC_LABELS[METRICS_LIST.FORECAST]}
                      </label>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="workspaceId"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-primary-700 font-normal text-sm">
                    Destination workspace
                  </FormLabel>
                  <Popover open={workspacePickerOpen} onOpenChange={setWorkspacePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        ref={triggerRef}
                        type="button"
                        variant="outline"
                        aria-haspopup="menu"
                        aria-expanded={workspacePickerOpen}
                        aria-controls={menuId}
                        aria-describedby={workspaceHelpId}
                        aria-label={
                          selectedWorkspace
                            ? `Destination workspace: ${selectedWorkspace.name}`
                            : 'Select a workspace'
                        }
                        className="w-full justify-between font-normal rounded-md min-h-14 border-input bg-background"
                      >
                        <span
                          className={cn('truncate', !selectedWorkspace && 'text-muted-foreground')}
                        >
                          {selectedWorkspace?.name ?? 'Select a workspace'}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden="true" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className={cn(
                        'w-[var(--radix-popover-trigger-width)] p-2',
                        WORKSPACE_PICKER_SURFACE,
                      )}
                    >
                      {hasWorkspaces ? (
                        <div
                          ref={menuRef}
                          id={menuId}
                          role="menu"
                          aria-label="Workspaces"
                          tabIndex={-1}
                          onKeyDown={onMenuKeyDown}
                          className="flex flex-col gap-0.5"
                        >
                          <div className="pb-1">
                            <Input
                              id={workspaceFilterId}
                              type="search"
                              className="min-h-0"
                              aria-label="Filter workspaces"
                              placeholder="Filter workspaces…"
                              value={workspaceFilter}
                              onChange={(event) => setWorkspaceFilter(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Escape') event.stopPropagation();
                              }}
                              startIcon={
                                <Search
                                  className="h-4 w-4 text-muted-foreground"
                                  aria-hidden="true"
                                />
                              }
                            />
                          </div>
                          <button
                            type="button"
                            role="menuitem"
                            data-active={field.value === null ? 'true' : undefined}
                            onClick={() => {
                              field.onChange(null);
                              setWorkspacePickerOpen(false);
                              setWorkspaceFilter('');
                            }}
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
                              field.value === null && 'bg-muted',
                            )}
                          >
                            <Check
                              className={
                                field.value === null
                                  ? 'h-4 w-4 shrink-0 text-primary'
                                  : 'h-4 w-4 shrink-0 invisible'
                              }
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1 truncate text-muted-foreground">
                              No workspace
                            </span>
                          </button>
                          {filteredWorkspaces.map((ws) => {
                            const isActive = ws.id === field.value;
                            return (
                              <button
                                key={ws.id}
                                type="button"
                                role="menuitem"
                                data-active={isActive ? 'true' : undefined}
                                onClick={() => {
                                  field.onChange(ws.id);
                                  setWorkspacePickerOpen(false);
                                  setWorkspaceFilter('');
                                }}
                                className={cn(
                                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
                                  isActive && 'bg-muted',
                                )}
                              >
                                <Check
                                  className={
                                    isActive
                                      ? 'h-4 w-4 shrink-0 text-primary'
                                      : 'h-4 w-4 shrink-0 invisible'
                                  }
                                  aria-hidden="true"
                                />
                                <span className="min-w-0 flex-1 truncate">{ws.name}</span>
                              </button>
                            );
                          })}
                          {filteredWorkspaces.length === 0 && (
                            <p className="px-2 py-1.5 text-sm text-muted-foreground">
                              No workspace matches your search.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 px-2 py-3 text-sm text-primary-700">
                          <p>You don&apos;t have any workspace yet.</p>
                          <p className="text-muted-foreground">
                            You can import without one, or create workspaces from the{' '}
                            <Link
                              to="/map"
                              className="font-medium text-brand-700 underline underline-offset-2"
                            >
                              Map
                            </Link>{' '}
                            page.
                          </p>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <p id={workspaceHelpId} className="text-xs text-muted-foreground">
                    Optional — without a workspace, the project is created standalone and can be
                    assigned to one later.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button" size="lg">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" size="lg" disabled={isSaveDisabled || !!uploadingFile}>
                {uploadingFile ? 'Uploading...' : 'Import and view'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
